import { useEffect, useState } from 'react';
import Subtitle from 'components/Subtitle';
import { useNavigate } from 'react-router';
import { fetchUnits, getUsers } from 'api/portal';
import Button from 'components/Button';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  GridLegacy as Grid,
  Typography,
  Radio,
  RadioGroup,
  TextField,
  Box,
} from '@mui/material';
import Autocomplete from 'components/Autocomplete';
import { formatToCpfUsername } from 'utils/format';
import { isCpf, validateEmail } from 'utils/validation';
import { useDispatch } from 'react-redux';
import { showErrorToast } from 'features/toastSlice';
import { useTranslation } from 'react-i18next';
import MemberUnits from 'components/MemberUnits';
import Permissions from 'components/Permissions';
import { filterPhoneChars } from 'utils/inputs';

function PersonalAndCorporateInfo({ errors, errorsMember, initialData, onSave }) {
  const [userId, setUserId] = useState(initialData.userId);

  const [users, setUsers] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);

  const [username, setUsername] = useState(initialData.document);
  const [firstName, setFirstName] = useState(initialData.firstName);
  const [lastName, setLastName] = useState(initialData.lastName);
  const [birthYear, setBirthYear] = useState(initialData.birthYear);
  const [notificationLanguage, setNotificationLanguage] = useState(initialData.notificationLanguage);

  const [isDocumentEmail, setIsDocumentEmail] = useState(initialData.isDocumentEmail ?? false);

  const [email, setEmail] = useState(
    initialData.isDocumentEmail && validateEmail(initialData.document) ? initialData.document : initialData.email,
  );
  const [isValidMemberEmail, setIsValidMemberEmail] = useState(true);

  const [phoneNumber, setPhoneNumber] = useState(initialData.phoneNumber);

  const [roles, setRoles] = useState(initialData.roles);

  const [selectedUnits, setSelectedUnits] = useState(initialData.units);
  const [units, setUnits] = useState([]);

  const [isLoadingUnits, setIsLoadingUnits] = useState(true);
  const [errorUnits, setErrorUnits] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const INVALID_EMAIL = t('messages.invalid-email');

  const clearUserData = () => {
    setSelectedUser(null);
    setUserId(null);
    setFirstName('');
    setLastName('');
    setBirthYear('');
    setNotificationLanguage('pt-br');
  };

  useEffect(() => {
    getUsers(new URLSearchParams())
      .then((response) => {
        setUsers(response.data);
      })
      .catch(() => {
        dispatch(showErrorToast('messages.fix-fields'));
      });
  }, [dispatch]);

  useEffect(() => {
    const searchParams = new URLSearchParams();

    searchParams.set('page_size', 9999);

    fetchUnits(searchParams)
      .then((response) => {
        setIsLoadingUnits(false);

        const { data } = response;

        setUnits(data.results);
        setErrorUnits(false);
      })
      .catch(() => {
        setIsLoadingUnits(false);
        setErrorUnits(true);
      });
  }, []);

  useEffect(() => {
    if (isDocumentEmail && validateEmail(username)) {
      setEmail(username);
    }
  }, [isDocumentEmail, username]);

  const onSelectUser = (value) => {
    if (value) {
      setSelectedUser(value);
      setUserId(value.id);
      setFirstName(value.first_name);
      setLastName(value.last_name);
      setIsDocumentEmail(false);
      return;
    }

    clearUserData();
  };

  const userOptions = users.map((user) => ({
    ...user,
    label: `${user.first_name} ${user.last_name}`,
  }));

  const handleSave = () => {
    if (!isValidMemberEmail) {
      return;
    }

    onSave({
      userId,
      document: username,
      firstName,
      lastName,
      birthYear,
      notificationLanguage,
      isDocumentEmail,
      email,
      phoneNumber,
      roles,
      units: selectedUnits,
    });
  };

  return (
    <>
      <Box maxWidth="428px" marginBottom="48px">
        <Subtitle>{t('organization.organization-members-create.title-sub-member-username')}</Subtitle>
        <Typography variant="body2" color="secondary" marginBottom="24px">
          {t('organization.organization-members-create.text-member-create-search-name')}
        </Typography>

        <Autocomplete
          disablePortal
          value={selectedUser}
          onChange={(event, newValue) => onSelectUser(newValue)}
          options={userOptions}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <Typography
              {...props}
              variant="body2"
              color="secondary"
              key={option.id}
              data-testid={`user-option-${option.id}`}
            >
              {option.label}
            </Typography>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid="name-text-field"
              label={t('braincare.organizations-create.step-add-users.dialog-input-user')}
            />
          )}
          data-testid="name-select"
        />

        <Grid container flexDirection="column" marginTop="16px" alignItems="center" justifyContent="center">
          <Grid item>
            <Typography variant="body2" color="secondary" fontWeight={600} marginBottom="24px">
              {t('organization.organization-members-create.text-member-create-or')}
            </Typography>
          </Grid>
          <Grid item>
            <Button color="gradient" onClick={clearUserData} data-testid="new-user-button">
              {t('organization.organization-members-create.button-new-user')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box maxWidth="428px" marginBottom="48px">
        <Subtitle>{t('organization.organization-members-create.title-sub-member-create')}</Subtitle>
        <Typography variant="body2" color="secondary" marginBottom="24px">
          {t('organization.organization-members-create.text-member-create-main')}
        </Typography>

        <TextField
          id="name"
          sx={{ margin: '0 0 16px 0' }}
          value={firstName}
          label={t('organization.organization-members-create.input-name')}
          variant="outlined"
          onChange={(event) => setFirstName(event.target.value)}
          data-testid="first-name-text-field"
          inputProps={{ 'data-testid': 'first-name-input' }}
          FormHelperTextProps={{ 'data-testid': 'first-name-error' }}
          error={errors.has('firstName')}
          helperText={errors.get('firstName')}
          disabled={userId !== null}
          fullWidth
        />

        <TextField
          id="last_name"
          sx={{ margin: '16px 0 16px 0' }}
          value={lastName}
          label={t('organization.organization-members-create.input-surname')}
          variant="outlined"
          onChange={(event) => setLastName(event.target.value)}
          data-testid="last-name-text-field"
          inputProps={{ 'data-testid': 'last-name-input' }}
          FormHelperTextProps={{ 'data-testid': 'last-name-error' }}
          error={errors.has('lastName')}
          helperText={errors.get('lastName')}
          disabled={userId !== null}
          fullWidth
        />

        {userId === null ? (
          <>
            <TextField
              id="document"
              sx={{ margin: '16px 0 16px 0' }}
              value={username}
              label={t('organization.organization-members-create.input-document')}
              variant="outlined"
              onChange={(event) => {
                const { value } = event.target;
                if (validateEmail(value)) {
                  setIsDocumentEmail(true);
                  setUsername(value);
                } else {
                  setIsDocumentEmail(false);
                  const formattedUsername = isCpf(value) ? formatToCpfUsername(value) : value;
                  setUsername(formattedUsername);
                }
              }}
              data-testid="document-text-field"
              inputProps={{ 'data-testid': 'document-input' }}
              FormHelperTextProps={{ 'data-testid': 'document-error' }}
              error={errors.has('document')}
              helperText={errors.get('document')}
              disabled={userId !== null}
              fullWidth
            />

            <TextField
              id="birth_year"
              sx={{ margin: '16px 0 16px 0' }}
              value={birthYear}
              label={t('account.me-edit-info.input-birth-year')}
              variant="outlined"
              onChange={(event) => setBirthYear(event.target.value.replace(/\D/g, ''))}
              data-testid="birth-year-text-field"
              inputProps={{ 'data-testid': 'birth-year-input' }}
              FormHelperTextProps={{ 'data-testid': 'birth-year-error' }}
              error={errors.has('birthYear')}
              helperText={errors.get('birthYear')}
              fullWidth
            />

            <FormControl>
              <FormLabel id="demo-controlled-radio-buttons-group">
                {t('auth.signup.text-doc-choice-language')}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue={notificationLanguage}
                value={notificationLanguage}
                onChange={(event) => setNotificationLanguage(event.target.value)}
                name="radio-buttons-group"
              >
                <FormControlLabel
                  value="pt-br"
                  control={<Radio inputProps={{ 'data-testid': 'pt-br-radio' }} />}
                  label={t('auth.signup.input-language-pt-br')}
                />
                <FormControlLabel
                  value="en-us"
                  control={<Radio inputProps={{ 'data-testid': 'en-us-radio' }} />}
                  label={t('auth.signup.input-language-en-us')}
                />
                <FormControlLabel
                  value="es-es"
                  control={<Radio inputProps={{ 'data-testid': 'es-es-radio' }} />}
                  label={t('auth.signup.input-language-es-es')}
                />
              </RadioGroup>
            </FormControl>
          </>
        ) : null}
      </Box>

      <Box maxWidth="428px" marginBottom="48px">
        <Subtitle>{t('organization.organization-members-create.title-sub-organization-info')}</Subtitle>
        <Typography variant="body2" color="secondary" marginBottom="24px">
          {t('organization.organization-members-create.text-sub-organization-info')}
        </Typography>

        <TextField
          id="email"
          sx={{ margin: '0 0 16px 0' }}
          value={email}
          placeholder="eg.: johndoe@yourorganization.com"
          label={t('organization.organization-members-create.input-email')}
          onChange={(event) => setEmail(event.target.value)}
          onBlur={() => setIsValidMemberEmail(email === '' || validateEmail(email))}
          variant="outlined"
          data-testid="email-text-field"
          inputProps={{ 'data-testid': 'email-input' }}
          error={!isValidMemberEmail || errorsMember.has('email')}
          helperText={
            (!isValidMemberEmail && INVALID_EMAIL) || (errorsMember.has('email') ? errorsMember.get('email') : '')
          }
          FormHelperTextProps={{ 'data-testid': 'email-error' }}
          fullWidth
        />

        <TextField
          id="phone"
          sx={{ margin: '16px 0 16px 0' }}
          value={phoneNumber}
          placeholder="+1 123-456-7890"
          label={t('organization.organization-members-create.input-phone')}
          onChange={(event) => setPhoneNumber(filterPhoneChars(event.target.value))}
          variant="outlined"
          data-testid="phone-number-text-field"
          inputProps={{ 'data-testid': 'phone-number-input' }}
          error={errorsMember.has('phoneNumber')}
          helperText={errorsMember.has('phoneNumber') ? errorsMember.get('phoneNumber') : ''}
          FormHelperTextProps={{ 'data-testid': 'phone-number-error' }}
          fullWidth
        />
      </Box>

      <Box marginBottom="48px" data-testid="roles-section">
        <Box>
          <Box>
            <Subtitle>{t('organization.organization-members-create.title-sub-member-roles')}</Subtitle>
            <Typography variant="body2" color="secondary" marginBottom="24px">
              {t('organization.organization-members-create.text-member-create-role')}
            </Typography>
          </Box>
        </Box>

        <Permissions
          selected={roles}
          onToggle={(selectedRoles) => setRoles(selectedRoles)}
          data-testid="roles-selection"
        />
        {errorsMember.has('roles') && (
          <Typography variant="body2" color="error" marginBottom="24px" data-testid="roles-selection-error">
            {errorsMember.get('roles')}
          </Typography>
        )}
      </Box>

      <Box marginBottom="48px">
        <Subtitle>{t('organization.organization-members-create.title-sub-member-units')}</Subtitle>
        <Typography variant="body2" color="secondary" marginBottom="24px">
          {t('organization.organization-members-create.text-member-create-units')}
        </Typography>

        <MemberUnits
          error={errorUnits}
          isLoading={isLoadingUnits}
          onToggle={(unitsSelected) => setSelectedUnits(unitsSelected)}
          selectedUnits={selectedUnits}
          units={units}
          data-testid="units-selection"
        />
        {errorsMember.has('units') && (
          <Typography variant="body2" color="error">
            {errorsMember.get('units')}
          </Typography>
        )}
      </Box>

      <Grid container spacing="16px">
        <Grid item>
          <Button
            color="gradient"
            onClick={handleSave}
            disabled={userId === undefined}
            data-testid="continue-button"
          >
            {t('organization.organization-members-create.button-continue')}
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={() => navigate('/organization/members')} data-testid="cancel-button">
            {t('organization.organization-members-create.button-cancel')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

export default PersonalAndCorporateInfo;
